import React, { useCallback, useMemo } from 'react'
import { useQuery } from 'react-query'
import { Button, Divider, Grid, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { apiJSON } from '@shared/utils/api/api.util'
import { SummaryTemplate, Template } from '@shared/types'
import { useFormContext } from 'react-hook-form'

type Props = {
  requirementBaseID: number
  type: 'auditorAssessment' | 'summary'
  onSelectTemplate: (template: Template | SummaryTemplate) => void
}

export const FormTemplatesGrid = ({
  requirementBaseID,
  type,
  onSelectTemplate,
}: Props) => {
  const classes = useStyles()
  const { watch } = useFormContext()
  const auditorStatusID = watch('auditorStatusID')
  const selectedTemplate = watch('selectedTemplate') as Template | undefined
  const endpoint = `templates?requirementBaseId=${requirementBaseID}`
  const { data: templates } = useQuery<Template[]>(endpoint, () =>
    apiJSON(endpoint)
  )

  const onTemplateClickFactory = useCallback(
    (template: Template | SummaryTemplate) => () => {
      onSelectTemplate(template)
    },
    [onSelectTemplate]
  )

  const filteredTemplates = useMemo(() => {
    if (type === 'auditorAssessment') {
      return templates?.filter(
        ({ status: { statusID: templateStatusID } }) =>
          templateStatusID === auditorStatusID
      )
    }

    if (type === 'summary' && selectedTemplate) {
      return selectedTemplate?.summaryTemplates || []
    }

    return []
  }, [templates, auditorStatusID, type, selectedTemplate])

  if (!filteredTemplates) {
    return null
  }

  return (
    <>
      <Divider className={classes.divider} />
      <Grid container>
        <Grid item xs={4}>
          <Typography variant="h6">Szablony</Typography>
        </Grid>
        <Grid item xs={8}>
          {filteredTemplates.length > 0 &&
            filteredTemplates.map((template) => (
              <Button
                key={
                  'summaryTemplateID' in template
                    ? template.summaryTemplateID
                    : template.descriptionTemplateID
                }
                onClick={onTemplateClickFactory(template)}
                variant="outlined"
                color="primary"
                fullWidth
                className={classes.templateButton}
              >
                {template.description}
              </Button>
            ))}
          {filteredTemplates.length === 0 && (
            <Typography variant="body1">
              Brak szablonów{' '}
              {type === 'auditorAssessment'
                ? 'dla wybranego wymagania'
                : 'dla podsumowania'}
            </Typography>
          )}
        </Grid>
      </Grid>
      <Divider className={classes.divider} />
    </>
  )
}

const useStyles = makeStyles((theme) => ({
  divider: {
    width: '100%',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  templateButton: {
    marginBottom: '20px',
    textAlign: 'left',
    textTransform: 'none',
  },
}))
